import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./EngineeringProductDesign.css";
import automationImage from "../../../images/service/img19.png";

type SolutionItem = { title: string; text: string };
type SolutionSection = {
  title: string;
  paragraphs: string[];
  items?: SolutionItem[];
  listTitle?: string;
};
type FrameworkSection = SolutionSection & {
  frameworkTitle?: string;
  principles?: string[];
};
type SolutionConfig = {
  pageTitle: string;
  variant: "team" | "ownership" | "lifecycle" | "productivity" | "realization";
  hero: { title: string; paragraphs: string[]; cta: string };
  overview: SolutionSection;
  framework: FrameworkSection;
  why: SolutionSection;
  cta: { title: string; paragraphs: string[]; cta: string };
};

const config: SolutionConfig = {
  "pageTitle": "Engineering Productivity & Design Automation",
  "hero": {
    "title": "Improve Engineering Efficiency. Accelerate Development.",
    "paragraphs": [
      "As semiconductor designs grow in complexity, engineering teams must deliver more while managing tighter schedules, increasing design iterations, and evolving technology requirements. Improving productivity is no longer just about working faster—it requires optimizing engineering workflows, reducing repetitive effort, and enabling teams to focus on high-value engineering activities that drive successful product development.",
      "Digicomm Semiconductor helps organizations improve engineering productivity through workflow optimization, design automation, and structured engineering methodologies. By streamlining development processes and integrating automation where it delivers the greatest value, we help customers enhance engineering efficiency, improve execution consistency, and accelerate semiconductor development with confidence."
    ],
    "cta": "Discuss Your Engineering Workflow"
  },
  "overview": {
    "title": "Optimizing Engineering Execution Through Smarter Workflows",
    "paragraphs": [
      "As semiconductor development programs scale in complexity, engineering productivity becomes a critical driver of project success. Manual processes, fragmented workflows, inconsistent methodologies, and repetitive engineering tasks can reduce efficiency, introduce avoidable errors, and slow overall program execution. Improving productivity requires more than automation—it requires a structured approach to engineering execution.",
      "Digicomm Semiconductor helps organizations streamline engineering workflows by combining process optimization, design automation, and engineering best practices into a unified productivity framework. Our approach enables teams to reduce manual effort, improve design consistency, enhance collaboration, and focus engineering resources on solving complex technical challenges."
    ],
    "listTitle": "Ideal Engagement Scenarios",
    "items": [
      {
        "title": "Workflow Optimization",
        "text": "Improve engineering efficiency by streamlining development processes, reducing bottlenecks, and creating more effective engineering workflows."
      },
      {
        "title": "Design Automation",
        "text": "Automate repetitive engineering activities to improve consistency, reduce manual effort, and enable engineers to focus on higher-value design and development tasks."
      },
      {
        "title": "Methodology Standardization",
        "text": "Establish repeatable engineering methodologies that improve collaboration, maintain quality standards, and ensure consistent execution across projects."
      },
      {
        "title": "Tool Integration",
        "text": "Enhance productivity by integrating engineering tools and workflows into a connected development environment that supports efficient program execution."
      },
      {
        "title": "Engineering Process Improvement",
        "text": "Continuously refine engineering practices through structured process improvements that enhance productivity, quality, and delivery performance."
      }
    ]
  },
  "framework": {
    "title": "Engineering Productivity Built into Every Workflow",
    "paragraphs": [
      "Improving engineering productivity requires more than introducing automation. It demands a structured framework that examines how engineering work is planned, executed, reviewed, and continuously refined. Digicomm's Engineering Productivity Framework combines workflow optimization, standardized methodologies, automation, and continuous improvement to help engineering organizations achieve greater efficiency without compromising technical quality.",
      "By aligning engineering processes with project objectives and integrating automation where it delivers measurable value, we enable customers to reduce operational complexity, improve execution consistency, and create scalable engineering environments that support long-term product development."
    ],
    "frameworkTitle": "Our Productivity Framework",
    "items": [
      {
        "title": "Assess Current Workflows",
        "text": "Evaluate existing engineering processes to identify inefficiencies, repetitive activities, workflow bottlenecks, and opportunities for productivity improvement."
      },
      {
        "title": "Optimize Engineering Processes",
        "text": "Refine engineering workflows, eliminate unnecessary complexity, and improve collaboration to create more efficient and predictable execution."
      },
      {
        "title": "Introduce Targeted Automation",
        "text": "Implement automation where it provides meaningful value by reducing manual effort, improving repeatability, and accelerating engineering activities."
      },
      {
        "title": "Standardize Engineering Methodologies",
        "text": "Develop consistent engineering practices and execution standards that improve collaboration, quality, and project scalability across teams."
      },
      {
        "title": "Drive Continuous Improvement",
        "text": "Monitor engineering performance, capture lessons learned, and continuously refine workflows to support sustained productivity gains and evolving project requirements."
      }
    ],
    "principles": [
      "Workflow-Driven Optimization",
      "Practical Automation",
      "Standardized Engineering Practices",
      "Integrated Tool Ecosystems",
      "Continuous Process Improvement",
      "Measurable Productivity Outcomes"
    ]
  },
  "why": {
    "title": "Why Organizations Choose Digicomm to Improve Engineering Productivity",
    "paragraphs": [
      "Improving engineering productivity requires a deep understanding of semiconductor development, engineering workflows, and execution challenges. Digicomm combines domain expertise with practical process optimization to help organizations improve the way engineering teams plan, collaborate, execute, and deliver. Our approach focuses on creating sustainable improvements that enhance productivity, strengthen engineering quality, and support long-term operational excellence."
    ],
    "listTitle": "Our Differentiators",
    "items": [
      {
        "title": "Semiconductor Engineering Expertise",
        "text": "Our understanding of semiconductor development enables us to optimize workflows that are technically aligned with the realities of complex engineering programs."
      },
      {
        "title": "Engineering-Led Process Optimization",
        "text": "We improve engineering execution by refining workflows, eliminating inefficiencies, and introducing practical improvements that support better collaboration and productivity."
      },
      {
        "title": "Practical Design Automation",
        "text": "Our automation approach focuses on delivering measurable engineering value by reducing repetitive effort, improving consistency, and enabling faster execution."
      },
      {
        "title": "Standardized Engineering Methodologies",
        "text": "Well-defined engineering practices and execution frameworks help improve repeatability, quality, and collaboration across teams and projects."
      },
      {
        "title": "Continuous Improvement Mindset",
        "text": "We work with customers to evaluate performance, identify new optimization opportunities, and evolve engineering practices as technologies and project requirements change."
      },
      {
        "title": "Productivity with Measurable Impact",
        "text": "Every engagement is focused on improving engineering efficiency, execution consistency, development velocity, and overall project performance through practical, measurable outcomes."
      }
    ]
  },
  "cta": {
    "title": "Build a More Productive Engineering Organization",
    "paragraphs": [
      "High-performing engineering teams are built on efficient workflows, disciplined methodologies, and a culture of continuous improvement. Whether you're looking to optimize engineering processes, automate repetitive tasks, standardize development practices, or improve execution efficiency, Digicomm Semiconductor helps you create an engineering environment that enables teams to deliver more with confidence, consistency, and quality."
    ],
    "cta": "Discuss Your Engineering Productivity Goals"
  },
  "variant": "productivity"
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TechIcon = ({ index = 0 }: { index?: number }) => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    {index % 5 === 0 && <>
      <rect x="13" y="13" width="22" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="19" y="19" width="10" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 7v6M24 7v6M30 7v6M18 35v6M24 35v6M30 35v6M7 18h6M7 24h6M7 30h6M35 18h6M35 24h6M35 30h6" stroke="currentColor" strokeWidth="1.6"/>
    </>}
    {index % 5 === 1 && <>
      <circle cx="24" cy="17" r="6" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="12" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="36" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M13 39c1-8 5-12 11-12s10 4 11 12M4 37c1-6 4-9 9-9M44 37c-1-6-4-9-9-9" fill="none" stroke="currentColor" strokeWidth="1.7"/>
    </>}
    {index % 5 === 2 && <>
      <path d="M9 34l9-10 8 6 13-17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M32 13h7v7" fill="none" stroke="currentColor" strokeWidth="2"/>
    </>}
    {index % 5 === 3 && <>
      <path d="M24 6l14 6v10c0 10-6 16-14 20C16 38 10 32 10 22V12z" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 24l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
    {index % 5 === 4 && <>
      <circle cx="24" cy="24" r="7" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M24 6v6M24 36v6M6 24h6M36 24h6M11 11l4 4M33 33l4 4M37 11l-4 4M15 33l-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
  </svg>
);

const Paragraphs = ({ items }: { items: string[] }) => (
  <>{items.map((p, i) => <p key={i}>{p}</p>)}</>
);

const HeroVisual = () => (
  <div className="sol-hero-visual productivity-hero-artwork">
    <img src={automationImage} alt="Automated engineering systems working across semiconductor circuitry" />
  </div>
);
const FrameworkVisual = () => {
  const items = config.framework.items || [];
  return (
    <div className={`sol-framework-visual sol-framework-visual--${config.variant}`} aria-hidden="true">
      <div className="sol-framework-center">
        <TechIcon index={config.variant === "team" ? 1 : config.variant === "ownership" ? 3 : config.variant === "productivity" ? 4 : 0}/>
      </div>
      {items.slice(0, 6).map((_, i) => (
        <span key={i} className={`sol-orbit sol-orbit--${i + 1}`}>
          <i>{String(i + 1).padStart(2, "0")}</i>
        </span>
      ))}
    </div>
  );
};

const Page: React.FC = () => (
  <Fragment>
    <div className={`solution-experience solution-experience--${config.variant} inner-page`}>
      <div className="body_wrap o-clip">
        <Header />
        <main>
          <PageTitle pageTitle={config.pageTitle} pagesub={config.pageTitle} />

          <section className="sol-hero">
            <div className="sol-shell sol-hero-layout">
              <div className="sol-copy sol-hero-copy">
                <h1>{config.pageTitle}</h1>
                <h2>{config.hero.title}</h2>
                <Paragraphs items={config.hero.paragraphs}/>
                {config.hero.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.hero.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <HeroVisual />
            </div>
          </section>

          <section className="sol-section sol-overview">
            <div className="sol-shell">
              <div className="sol-heading-layout">
                <div className="sol-copy"><h2>{config.overview.title}</h2></div>
                <div className="sol-copy"><Paragraphs items={config.overview.paragraphs}/></div>
              </div>
              {config.overview.listTitle && <h3 className="sol-list-heading">{config.overview.listTitle}</h3>}
              <div className="sol-scenario-grid">
                {(config.overview.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span className="sol-index">{String(i + 1).padStart(2, "0")}</span>
                    <div className="sol-icon"><TechIcon index={i}/></div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="sol-section sol-framework">
            <div className="sol-shell">
              <div className="sol-framework-top">
                <div className="sol-copy">
                  <h2>{config.framework.title}</h2>
                  <Paragraphs items={config.framework.paragraphs}/>
                </div>
                <FrameworkVisual />
              </div>

              {config.framework.frameworkTitle && <h3 className="sol-list-heading">{config.framework.frameworkTitle}</h3>}

              <div className="sol-framework-steps">
                {(config.framework.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <div><h3>{item.title}</h3><p>{item.text}</p></div>
                  </article>
                ))}
              </div>

              {!!config.framework.principles?.length && (
                <div className="sol-principles">
                  {config.framework.principles.map((x) => <span key={x}>{x}</span>)}
                </div>
              )}
            </div>
          </section>

          <section className="sol-section sol-why">
            <div className="sol-shell sol-why-layout">
              <div className="sol-copy">
                <h2>{config.why.title}</h2>
                <Paragraphs items={config.why.paragraphs}/>
              </div>

              <div className={`sol-value-system sol-value-system--${config.variant}`}>
                <div className="sol-value-center">
                  <TechIcon index={config.variant === "productivity" ? 4 : config.variant === "team" ? 1 : 0}/>
                  <strong>{config.pageTitle}</strong>
                </div>
                <div className="sol-value-grid">
                  {(config.why.items || []).map((item, i) => (
                    <article key={item.title}>
                      <div className="sol-icon"><TechIcon index={i}/></div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="sol-cta">
            <div className="sol-shell sol-cta-layout">
              <div className="sol-copy">
                <h2>{config.cta.title}</h2>
                <Paragraphs items={config.cta.paragraphs}/>
                {config.cta.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.cta.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <div className="sol-cta-visual" aria-hidden="true">
                <div className="sol-globe"><i/><i/><i/><i/><i/></div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <Scrollbar />
      </div>
    </div>
  </Fragment>
);

export default Page;
